import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

export default function Impressions() {
  const { t } = useLanguage();
  const [impressions, setImpressions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    const fetchImpressions = async () => {
      try {
        const res = await axios.get(`${API}/impressions`);
        setImpressions(res.data.impressions || []);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchImpressions();
  }, []);

  const filtered = filter === 'all'
    ? impressions
    : impressions.filter((imp) => imp.category === filter);

  const categories = ['all', 'export', 'preparation', 'delivery'];

  return (
    <div className="min-h-screen pt-28 md:pt-32 pb-24" data-testid="impressions-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block mb-3">
            Partners4Cars
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-white font-[Chivo]">
            {t('impressions.title')}
          </h1>
          <p className="text-base md:text-lg text-zinc-500 mt-4 max-w-2xl">
            {t('impressions.subtitle')}
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-4 mb-12 border-b border-white/5 pb-4" data-testid="impressions-filters">
          {categories.map((cat) => (
            <Button
              key={cat}
              variant="ghost"
              onClick={() => setFilter(cat)}
              data-testid={`filter-${cat}`}
              className={`text-xs uppercase tracking-[0.2em] rounded-sm px-4 h-9 ${
                filter === cat
                  ? 'text-white bg-white/5'
                  : 'text-zinc-600 hover:text-white'
              }`}
            >
              {t(`impressions.${cat}`)}
            </Button>
          ))}
        </div>

        {/* Gallery Masonry */}
        {loading ? (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#121212] border border-zinc-800/50 rounded-md animate-pulse break-inside-avoid">
                <div className="aspect-[4/3] bg-zinc-800/50 rounded-t-md" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-zinc-800/50 rounded w-3/4" />
                  <div className="h-3 bg-zinc-800/50 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-zinc-500">{t('admin.noData')}</p>
          </div>
        ) : (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
            {filtered.map((imp, i) => (
              <motion.div
                key={imp.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="break-inside-avoid"
              >
                <div
                  className="bg-[#121212] border border-zinc-800/50 rounded-md overflow-hidden group hover:border-[#ED2227]/20 transition-all duration-300"
                  data-testid={`impression-card-${imp.id}`}
                >
                  {/* YouTube Video */}
                  {imp.video_url && getYouTubeId(imp.video_url) && (
                    <div className="aspect-video">
                      <iframe
                        src={`https://www.youtube.com/embed/${getYouTubeId(imp.video_url)}`}
                        title={imp.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                  {/* Images */}
                  {imp.images?.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setLightboxImg(img)}
                      className="w-full cursor-pointer"
                    >
                      <img
                        src={img}
                        alt={imp.title}
                        className="w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                        loading="lazy"
                      />
                    </button>
                  ))}
                  <div className="p-5">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#ED2227] block mb-2">
                      {imp.category}
                    </span>
                    <h3 className="text-lg font-bold text-white font-[Chivo] mb-2">{imp.title}</h3>
                    <p className="text-sm text-zinc-500 leading-relaxed">{imp.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
          data-testid="lightbox"
        >
          <img
            src={lightboxImg}
            alt="Full view"
            className="max-w-full max-h-[90vh] object-contain rounded-md"
          />
        </div>
      )}
    </div>
  );
}
